import { Modal, Form, Input, Select, message } from "antd";
import { useCreateExpenseDescription } from "@/pages/Expenses/ManageExpense/hooks/useExpenseDescriptions";
import { useGetExpenseTypesOptions } from "@/pages/Expenses/ManageExpense/hooks/useExpenseTypes";
import { useEffect, useMemo } from "react";
import { SelectOption } from "@/types/select";
import { CreateExpenseDescriptionFormValues } from "@/pages/Expenses/ManageExpense/api/expenseDescriptions";
import { useGetExpenseUnits } from "@/pages/Expenses/RecordExpense/hooks/useExpenses";

interface CreateModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

const CreateModal = ({ isOpen, onClose, onSuccess }: CreateModalProps) => {
  const [form] = Form.useForm();
  const expenseTypeId = Form.useWatch("expense_type_id", form);
  const { mutate: createExpenseDescription, isPending } =
    useCreateExpenseDescription();
  const { data: expenseTypes } = useGetExpenseTypesOptions();
  const { data: units } = useGetExpenseUnits({
    config: {
      params: {
        limit: -1,
        include_inactive: false,
      },
    },
    enabled: isOpen,
  });

  const expenseTypeOptions: SelectOption[] = (expenseTypes || [])
    .filter((type) => type.is_active)
    .map((type) => ({
      label: type.name,
      value: type.id,
    }));

  const unitOptions: SelectOption[] = (units || [])
    .filter((unit) => unit.is_active)
    .map((unit) => ({
      label: unit.name,
      value: unit.id,
    }));

  // Hide unit if not required in expense type
  const isUnitHidden = useMemo(() => {
    if (!expenseTypeId) {
      return true;
    }

    const isUnitRequired = expenseTypes
      ?.find((type) => type.id === expenseTypeId)
      ?.required_fields?.includes("unit");

    return !isUnitRequired;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, expenseTypeId]);

  const handleSubmit = async (values: CreateExpenseDescriptionFormValues) => {
    createExpenseDescription(values, {
      onSuccess: () => {
        message.success("Successfully created expense description");
        form.resetFields();
        onClose?.();
        onSuccess?.();
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  return (
    <Modal
      title="New Description"
      open={isOpen}
      onCancel={handleCancel}
      width={600}
      confirmLoading={isPending}
      onOk={form.submit}
      okText="Create"
      destroyOnHidden
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="expense_type_id"
          label="Expense Type"
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Select expense type"
            options={expenseTypeOptions}
          />
        </Form.Item>
        <Form.Item
          name="unit_id"
          label="Unit"
          rules={[{ required: !isUnitHidden }]}
          hidden={isUnitHidden}
        >
          <Select placeholder="Select unit" options={unitOptions} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateModal;
